import {Component, EventEmitter, Input, OnChanges, OnInit, Output} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {ToastrService} from 'ngx-toastr';
import {environment} from '../../../environments/environment';
import {FloorInfoSet4} from '../../shared/models/models';
import {MatRipple} from "@angular/material/core";
import {NgIf} from "@angular/common";
import {MatTooltip} from "@angular/material/tooltip";
import {FormControl} from "@angular/forms";

@Component({
  selector: 'import-button',
  template: `
    <div
      class="btn btn-light"
      matRipple
      [matTooltip]="tooltipMessage"
      matTooltipPosition="above"
      [class.disable]="!isButtonActive"
      (click)="onClicked()"
    >
      <ng-container *ngIf="!loading">
        <span class="upload-label">Импорт шахматки из файла</span>
      </ng-container>
      <ng-container *ngIf="loading">
        <div class="loading"></div>
      </ng-container>
    </div>
  `,
  styles: [
    `
      .upload-label {
        cursor: pointer;
        width: 100%;
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 1;
      }

      .btn {
        margin-top: 5px;
      }

      .btn-light {
        background-color: #fff;
        color: #000;
        box-shadow: 0 0 10px -2px rgba(0, 0, 0, 0.5);
      }

      .disable {
        background: #c5c5c5;
        cursor: not-allowed;
        box-shadow: none;
      }
    `
  ],
  imports: [
    MatRipple,
    MatTooltip,
    NgIf,
  ],
  standalone: true
})
export class ImportButtonComponent implements OnInit, OnChanges {
  @Input() isPristine: boolean;
  @Input() buildingID: number = 0;
  @Input() floors: number = 0;
  @Input() flats: FloorInfoSet4[] = [];
  @Output() updateSpot = new EventEmitter();
  @Input() control: FormControl;

  loading = false;

  get isButtonActive(): boolean {
    if (!this.control) return false;

    const hasSaleDate = this.flats.some((flat) => flat.saleDate !== null);
    if (hasSaleDate) {
      return false;
    }

    return this.control.valid;
  }

  get tooltipMessage(): string {
    if (!this.control) return '';

    const hasSaleDate = this.flats.some((flat) => flat.saleDate !== null);
    if (hasSaleDate) {
      return 'В пятне уже есть реализованные квартиры, импорт не возможен!';
    }

    if (this.control.invalid) {
      return 'Заполните все обязательные поля в пятне!';
    }
    return '';
  }

  private apiUrl = environment.apiUrl + 'BuildingFlatSales/InsBuildingFlatSalesFile';

  constructor(private http: HttpClient, private toastr: ToastrService) {
  }

  ngOnInit(): void {
  }

  ngOnChanges(): void {
  }

  async onClicked(): Promise<void> {
    if (!this.isButtonActive) return;

    const hasFlats = this.flats.length > 0 && !this.flats.some((flat) => flat.saleDate !== null);

    if (hasFlats) {
      const confirmResult = window.confirm('В пятне существует квартиры, вы хотите их заменить?');
      if (!confirmResult) return;
    }

    try {
      const file = await this.selectFile();
      if (file) {
        this.uploadFile(file);
      }
    } catch (err) {
      console.error('Ошибка выбора файла:', err);
    }
  }

  private async selectFile(): Promise<File | null> {
    try {
      const [fileHandle] = await window.showOpenFilePicker({
        types: [
          {
            description: 'Excel Files',
            accept: {
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
              'application/vnd.ms-excel': ['.xls']
            }
          }
        ],
        multiple: false
      });
      return await fileHandle.getFile();
    } catch (err) {
      console.error('Выбор файла отменен.', err);
      return null;
    }
  }

  private uploadFile(file: File): void {
    if (!file.name.endsWith('.xls') && !file.name.endsWith('.xlsx')) {
      alert('Пожалуйста, выберите файл Excel.');
      return;
    }

    this.loading = true;

    const formData = new FormData();
    formData.append('Attachment', file);
    formData.append('BuildingID', this.buildingID.toString());

    this.http.post(this.apiUrl, formData).subscribe({
      next: () => {
        this.toastr.success('Файл успешно загружен!');
        this.updateSpot.emit(true);
      },
      error: (err) => {
        console.error(err);
        this.toastr.error('Ошибка при загрузке файла.');
        this.loading = false;
      },
      complete: () => {
        this.loading = false;
      }
    });
  }
}

declare global {
  interface Window {
    showOpenFilePicker?: (options?: any) => Promise<any[]>;
  }
}
